import { MouseEvent, useEffect, useState } from "react"
import { useAskSpecificQuestion, useGetQuestions, useAskCustomQuestion } from "hooks/api/aiAgent/aiAgent.hook"
import { useNatsAnswerStream } from "hooks/nats.hook"
import { useBackendChatHistoryStore, useChatStore } from "store/aiAgent.store"
import { useSearchParams } from "react-router-dom"
import { useNatsCreds } from "hooks/api/user/user.hook"
import { QuestionInput } from "components/ui/inputs/QuestionInput/QuestionInput"
import { PredefinedQuestions } from "./layout/PredefinedQuestions/PredefinedQuestions"
import { ChatHistory } from "./layout/ChatHistory/ChatHistory"
import { generateNatsSubject } from "helpers/utils.helper"
import { TOnChangeInput } from "types/default.type"
import type { TToolCallInfo } from "types/aiAgent.type"
import style from "./Chat.module.scss"

const { chat, bubble, answer } = style

const natsUrl = import.meta.env.VITE_NATS_URL

export const Chat = () => {
	const { mutate: askSpecificQuestion } = useAskSpecificQuestion()
	const { mutate: askCustomQuestionMutation, isPending: isAskCustomQuestionPending } = useAskCustomQuestion()
	const [searchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const { data: questionsData } = useGetQuestions(drawingId)
	const { data: natsCreds } = useNatsCreds()
	const { chatHistory, addQuestion, updateLastQuestionTraceId, setToolCallInfo, setFailedAnswer } = useChatStore()
	const { replaceBackendChatHistory, backendChatHistory, clearBackendChatHistory } = useBackendChatHistoryStore()

	const [subject, setSubject] = useState("")
	const [questionId, setQuestionId] = useState<number>()
	const [customQuestion, setCustomQuestion] = useState("")

	const streamingAnswer = useNatsAnswerStream(natsUrl, subject, natsCreds)
	const streamingInProgress =
		streamingAnswer?.status === "STARTED" ||
		streamingAnswer?.status === "RUNNING" ||
		streamingAnswer?.status === "PREDEFINED_QUESTION_SUMMARY" ||
		streamingAnswer?.status === "TOOL_CALL_INFO"
	const customQuestionInProgress = isAskCustomQuestionPending || streamingInProgress

	// Set the chat history when the streaming answer is completed
	useEffect(() => {
		if (streamingAnswer?.status === "COMPLETED" && streamingAnswer.messages) {
			replaceBackendChatHistory(streamingAnswer.messages)
		}
	}, [replaceBackendChatHistory, streamingAnswer])

	// Set the tool call info when the streaming answer is in TOOL_CALL_INFO status
	useEffect(() => {
		if (streamingAnswer?.status === "TOOL_CALL_INFO") {
			const toolCallMessage = streamingAnswer.message as unknown as TToolCallInfo
			setToolCallInfo(toolCallMessage)
		}
	}, [streamingAnswer, setToolCallInfo])

	useEffect(() => {
		if (drawingId) {
			const natsSubject = generateNatsSubject()
			setSubject(natsSubject) // Set the NATS subject based on the drawing ID
		}
		clearBackendChatHistory() // Clear backend chat history when drawingId changes
	}, [drawingId, clearBackendChatHistory])

	// Ask a predefined question
	const askPredefinedQuestionHandle = (e: MouseEvent<HTMLElement>, questionId: number, question: string) => {
		if (streamingInProgress) {
			e.preventDefault()
			e.stopPropagation()
			return
		}
		addQuestion(question, "predefined")
		setQuestionId(questionId)

		askSpecificQuestion(
			{ drawingId, questionId, subject, messages: backendChatHistory || [] },
			{
				onSuccess: (response) => {
					updateLastQuestionTraceId(response.data.langsmith_trace_id)
				},
				onError: () => {
					setFailedAnswer()
				},
			},
		)
	}

	// Handle input change for custom question
	const changeCustomQuestionInputHandle = (e: TOnChangeInput) => {
		const value = e.target.value
		setCustomQuestion(value)
	}

	// Get the last custom question's trace ID for continuation
	const getLastCustomQuestionTraceId = () => {
		const customQuestions = chatHistory.questions.filter((q) => q.type === "custom")
		return customQuestions.length > 0 ? customQuestions[customQuestions.length - 1].langsmithTraceId : undefined
	}

	// Ask a specific question
	const askCustomQuestionHandle = () => {
		//  Check if the custom question is not empty and send the question
		if (customQuestion !== "") {
			addQuestion(customQuestion, "custom") // Add the question to the chat history
			askCustomQuestionMutation(
				{
					drawingId,
					subject,
					langsmithTraceId: getLastCustomQuestionTraceId(), // Use previous custom question's trace ID for continuation
					question: customQuestion,
					messages: backendChatHistory || [],
				},
				{
					onSuccess: (response) => {
						updateLastQuestionTraceId(response.data.langsmith_trace_id)
					},
					onError: () => {
						setFailedAnswer()
					},
				},
			)
		}

		setCustomQuestion("") // Clear the input field after sending the question
	}

	return (
		<div className={chat}>
			<div className={`${bubble} ${answer}`}>
				<span>Hello!</span>
			</div>
			<PredefinedQuestions
				predefinedQuestions={questionsData?.predefined_questions}
				customChat={questionsData?.custom_chat}
				streamingInProgress={streamingInProgress}
				askQuestionHandle={askPredefinedQuestionHandle}
			/>
			<ChatHistory
				chatHistory={chatHistory}
				streamingAnswer={streamingAnswer}
				drawingId={drawingId}
				questionId={questionId}
				predefinedQuestions={questionsData?.predefined_questions}
				subject={subject}
				backendChatHistory={backendChatHistory}
			/>
			{questionsData?.custom_chat.status === "success" ? (
				<QuestionInput
					value={customQuestion}
					sendMessageHandle={askCustomQuestionHandle}
					onChange={changeCustomQuestionInputHandle}
					disabled={customQuestionInProgress}
				/>
			) : null}
		</div>
	)
}
